/* IL LEGAME FRA UN TRADEBOT E IL BACKTEST CHE LO GIUSTIFICA.
 *
 * La CHIAVE del riconoscimento non sta più qui: sta in `lib/chiaveConfig.ts`,
 * perché la leggono in due — questa schermata, che dal bot risale al backtest,
 * e la lista dei backtest, che dal backtest risale al bot. Là ci sono anche il
 * perché delle due normalizzazioni (token avvolti e stablecoin equiparate) e la
 * nota che quella è la versione verificata in produzione.
 *
 * Qui resta la parte che riguarda solo il tradebot: come si interroga
 * `/backtester/search` per trovare la riga corrispondente, e i quattro esiti
 * possibili di quella ricerca.
 *
 * Le tre firme originali si riespongono da qui: chi già importava
 * `chiaveConfig` o le normalizzazioni da questo file non deve cambiare import
 * per una funzione che si è solo spostata.
 */

import { backtest, usaRichiesta } from '../../api';
import type { BacktestRiga, Tradebot } from '../../api';
import { SEP_CHIAVE, chiaveConfig, normalizzaAsset } from '../../lib/chiaveConfig';

export {
  chiaveConfig,
  normalizzaAsset,
  normalizzaValuta,
  SEP_CHIAVE,
} from '../../lib/chiaveConfig';
export type { ConfigurazioneConfrontabile } from '../../lib/chiaveConfig';

/* ==========================================================================
 * La ricerca del backtest corrispondente
 * ======================================================================== */

/**
 * Quattro esiti, non due.
 *
 * "Non trovato" da solo sarebbe una bugia per omissione: non dice se il
 * backtest non esiste, se non l'abbiamo cercato abbastanza a fondo, o se il
 * bot non è nemmeno confrontabile. Sono tre cose diverse e portano a tre
 * decisioni diverse — e nessuna delle tre è "storia insufficiente", che è il
 * quarto caso ancora, quello che riguarda il backtest una volta trovato.
 */
export type StatoCorrelazione =
  /** Trovato: esiste un backtest con esattamente questi parametri. */
  | 'trovato'
  /** Cercato fino in fondo, non c'è: questa configurazione non è mai stata provata. */
  | 'assente'
  /** La ricerca si è fermata prima della fine: non si può concludere nulla. */
  | 'troncata'
  /** Il bot non ha `strategyParams`: non c'è niente da confrontare. */
  | 'non-confrontabile';

export interface Correlazione {
  stato: StatoCorrelazione;
  riga: BacktestRiga | null;
  /** Quante righe di backtest sono state guardate davvero. */
  esaminate: number;
  /** Quante ce n'erano in tutto, secondo il server. */
  totali: number;
  /** Quanti backtest hanno esattamente questa chiave: più di uno è normale. */
  candidati: number;
}

const NON_CONFRONTABILE: Correlazione = {
  stato: 'non-confrontabile',
  riga: null,
  esaminate: 0,
  totali: 0,
  candidati: 0,
};

/** Il server taglia comunque a 200 per pagina. */
export const RIGHE_PER_PAGINA = 200;

/** Quante pagine scorrere per ogni asset. Il dettaglio guarda un bot solo e
 *  può permettersi di scavare; la lista ne ha una manciata e si ferma prima,
 *  dichiarando che si è fermata. */
export const PAGINE_DETTAGLIO = 5;
export const PAGINE_LISTA = 1;

/**
 * Fra più backtest con la stessa chiave si preferisce quello dentro un gruppo.
 *
 * È l'originale — la griglia da cui la configurazione è stata scelta — ed è
 * l'unico che porta con sé l'intorno: un backtest singolo non ha vicini con cui
 * confrontarsi, quindi il suo `intorno` è sempre `null`. A parità, il più
 * recente, perché è quello ricalcolato con le metriche attuali.
 */
export function scegliBacktest(candidati: BacktestRiga[]): BacktestRiga | null {
  if (candidati.length === 0) return null;
  return [...candidati].sort((a, b) => {
    const gruppoA = a.backtestGroupId != null ? 1 : 0;
    const gruppoB = b.backtestGroupId != null ? 1 : 0;
    if (gruppoA !== gruppoB) return gruppoB - gruppoA;
    return b.id - a.id;
  })[0];
}

/**
 * Gli asset da chiedere al server per un bot.
 *
 * Il filtro `asset` di `/backtester/search` è un confronto esatto e non sa
 * nulla di token avvolti: per un bot su `BTCB` bisogna chiedere sia `BTC` —
 * dove stanno quasi sempre i backtest — sia `BTCB`, nel caso raro in cui il
 * backtest sia stato fatto sul token avvolto. Due chiamate invece di una, ma
 * un falso "nessuna corrispondenza" costa molto di più.
 */
function assetDaChiedere(asset: string): string[] {
  return [...new Set([normalizzaAsset(asset), String(asset).toUpperCase()])];
}

interface Lotto {
  strategyName: string;
  candleSize: number;
  assets: string[];
  bots: Tradebot[];
}

/** Bot che condividono strategia, candela e asset normalizzato condividono
 *  anche le query: si interrogano una volta sola per tutti. */
function raggruppa(bots: Tradebot[]): Lotto[] {
  const lotti = new Map<string, Lotto>();

  for (const b of bots) {
    const chiave = [b.strategyName, normalizzaAsset(b.asset), b.candleSize].join(SEP_CHIAVE);
    const esistente = lotti.get(chiave);
    if (esistente) {
      esistente.bots.push(b);
      for (const a of assetDaChiedere(b.asset)) {
        if (!esistente.assets.includes(a)) esistente.assets.push(a);
      }
    } else {
      lotti.set(chiave, {
        strategyName: b.strategyName,
        candleSize: b.candleSize,
        assets: assetDaChiedere(b.asset),
        bots: [b],
      });
    }
  }

  return [...lotti.values()];
}

/**
 * Cerca i backtest corrispondenti a un insieme di bot.
 *
 * `/backtester/search` non sa filtrare per `strategyParams`: i filtri più
 * stretti che accetta sono strategia, asset e candela, e dentro quel taglio può
 * esserci un'intera griglia da migliaia di righe. Si scorre a pagine fino a un
 * tetto, e se il tetto arriva prima della fine l'esito è `troncata` — mai
 * `assente`, che sarebbe una conclusione non guadagnata.
 */
export async function cercaCorrelazioni(
  bots: Tradebot[],
  paginePerAsset: number,
  segnale?: AbortSignal,
): Promise<Map<number, Correlazione>> {
  const esiti = new Map<number, Correlazione>();
  if (bots.length === 0) return esiti;

  await Promise.all(
    raggruppa(bots).map(async (lotto) => {
      const chiavi = new Map<number, string>();
      for (const b of lotto.bots) {
        const k = chiaveConfig(b);
        if (k) chiavi.set(b.id, k);
        else esiti.set(b.id, { ...NON_CONFRONTABILE });
      }
      if (chiavi.size === 0) return;

      const raccolti = new Map<string, BacktestRiga[]>();
      let esaminate = 0;
      let totali = 0;

      for (const asset of lotto.assets) {
        for (let pagina = 1; pagina <= paginePerAsset; pagina += 1) {
          const risposta = await backtest.cerca(
            {
              strategyName: lotto.strategyName,
              asset,
              candleSize: lotto.candleSize,
              page: pagina,
              limit: RIGHE_PER_PAGINA,
              /* Ordinamento per id: stabile fra una pagina e l'altra. Ordinare
                 per profitto farebbe scorrere le righe sotto i piedi se nel
                 frattempo qualcuno ricalcola le metriche, e la stessa riga
                 comparirebbe due volte o mai. */
              sortBy: 'id',
              sortDir: 'DESC',
            },
            segnale,
          );

          if (pagina === 1) totali += risposta.total;
          esaminate += risposta.data.length;

          for (const riga of risposta.data) {
            const k = chiaveConfig(riga);
            if (!k) continue;
            const gia = raccolti.get(k);
            if (gia) gia.push(riga);
            else raccolti.set(k, [riga]);
          }

          if (pagina >= risposta.pageCount) break;
        }
      }

      for (const [idBot, chiave] of chiavi) {
        const candidati = raccolti.get(chiave) ?? [];
        esiti.set(idBot, {
          stato: candidati.length > 0 ? 'trovato' : esaminate >= totali ? 'assente' : 'troncata',
          riga: scegliBacktest(candidati),
          esaminate,
          totali,
          candidati: candidati.length,
        });
      }
    }),
  );

  return esiti;
}

/* ==========================================================================
 * L'hook
 * ======================================================================== */

/**
 * Le correlazioni di una manciata di bot, con i quattro stati della richiesta.
 *
 * Le dipendenze sono la FIRMA degli id, non l'array: `usaRichiesta` sparge le
 * dipendenze in `useEffect`, e un array ricreato a ogni render rilancerebbe la
 * ricerca all'infinito. La funzione arriva comunque fresca a ogni giro, quindi
 * legge sempre i bot correnti.
 */
export function usaCorrelazioni(bots: Tradebot[], paginePerAsset: number) {
  const firma = bots.map((b) => b.id).join(',');

  return usaRichiesta<Map<number, Correlazione>>(
    (segnale) => cercaCorrelazioni(bots, paginePerAsset, segnale),
    [firma, paginePerAsset],
    { attiva: bots.length > 0, eVuoto: () => false },
  );
}
